import clsx from 'clsx';
import { ArrowRight } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Icon, ModuleAccent } from '@/app/router/modules';

/**
 * A navigation control shaped as a card: the whole card is one link, its
 * accessible name is the title, and the description is attached as its
 * description — so a screen reader hears "Students, link, Manage student
 * records…" rather than a pile of loose text.
 *
 * `size="lg"` is the launcher's module card; `"md"` the module dashboard's
 * sub-module card.
 */
export function ModuleCard({
  id,
  title,
  description,
  icon: IconComponent,
  to,
  accent = 'blue',
  badge,
  size = 'md',
}: {
  /** Stable id, used to wire the description to the link. */
  id: string;
  title: string;
  description: string;
  icon: Icon;
  to: string;
  accent?: ModuleAccent;
  /** A real figure from the server, or nothing. Never a placeholder. */
  badge?: ReactNode;
  size?: 'md' | 'lg';
}) {
  const descriptionId = `${id}-description`;
  const style = { '--mod': `var(--os-${accent})`, '--mod-soft': `var(--os-${accent}-soft)` } as CSSProperties;

  return (
    <Link
      to={to}
      style={style}
      aria-describedby={descriptionId}
      className={clsx(
        'group relative flex h-full flex-col rounded-xl border border-line bg-card text-left shadow-xs',
        'transition-[box-shadow,border-color,transform] duration-150 ease-out',
        'hover:border-line-strong hover:shadow-md motion-safe:hover:-translate-y-0.5',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--sms-accent-ring)]',
        size === 'lg' ? 'gap-4 p-5' : 'gap-3 p-4',
      )}
    >
      {/* A thin accent line along the top edge: identity without a gradient. */}
      <span
        aria-hidden="true"
        className="absolute inset-x-4 top-0 h-[2px] rounded-b-full bg-[var(--mod)] opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100"
      />

      <div className="flex items-start justify-between gap-3">
        <span
          aria-hidden="true"
          className={clsx(
            'flex shrink-0 items-center justify-center rounded-lg bg-[var(--mod-soft)] text-[var(--mod)]',
            'transition-transform duration-150 ease-out motion-safe:group-hover:scale-105',
            size === 'lg' ? 'h-11 w-11' : 'h-9 w-9',
          )}
        >
          <IconComponent className={size === 'lg' ? 'h-[22px] w-[22px]' : 'h-[18px] w-[18px]'} />
        </span>
        <ArrowRight
          aria-hidden="true"
          className="mt-1 h-4 w-4 shrink-0 text-subtle transition-[transform,color] duration-150 group-hover:text-ink motion-safe:group-hover:translate-x-0.5"
        />
      </div>

      <div className="min-w-0 flex-1">
        <h3 className={clsx('font-semibold tracking-[-0.01em] text-ink', size === 'lg' ? 'text-[16px]' : 'text-[14.5px]')}>
          {title}
        </h3>
        <p id={descriptionId} className="mt-1 text-[13px] leading-snug text-muted">
          {description}
        </p>
      </div>

      {badge !== undefined && badge !== null && (
        <div className="text-[12px] font-medium text-muted">{badge}</div>
      )}
    </Link>
  );
}
